import { api } from '../client';

import type { ListResponse, PaginationParams, StockAuditEvent } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

export type TransferStatus = 'draft' | 'confirmed';

/** Transfer header */
export interface Transfer {
  id: string;
  from_location_id: string;
  to_location_id: string;
  status: TransferStatus;
  transferred_at: string;
  notes?: string | null;
  items_count?: number | null;
  unit_totals?: Record<string, string> | null;
  reference_total_amount?: string | null;
  items: TransferItem[];
  created_at?: string | null;
  created_by?: string | null;
  current_revision_no?: number | null;
  current_revision_id?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
}

/** Transfer item */
export interface TransferItem {
  id: string;
  transfer_id: string;
  product_id: string;
  quantity: string;
  unit_code: string;
  from_balance_before?: string | null;
  from_balance_after?: string | null;
  to_balance_before?: string | null;
  to_balance_after?: string | null;
}

export interface TransferListSummary {
  by_status: Record<string, number>;
}

export interface TransferDetailResponse {
  request_id: string;
  transfer: Transfer;
  audit_events?: StockAuditEvent[];
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

/** Transfer filters */
export interface TransferFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  status?: string;
  location_id?: string;
  from_location_id?: string;
  to_location_id?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  include_summary?: boolean;
}

/** Transfers response */
export interface TransfersResponse extends ListResponse<Transfer> {
  summary?: TransferListSummary | null;
}

/** Create transfer request */
export interface CreateTransferRequest {
  from_location_id: string;
  to_location_id: string;
  transferred_at: string;
  notes?: string;
  items: CreateTransferItemRequest[];
  initial_status?: 'draft' | 'confirmed';
}

/** Create transfer item */
export interface CreateTransferItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
}

export interface UpdateTransferRequest {
  from_location_id: string;
  to_location_id: string;
  transferred_at: string;
  notes?: string;
  items: CreateTransferItemRequest[];
  change_reason?: string;
}

export interface CancelTransferRequest {
  cancel_reason?: string;
}

type TransferStockDocumentEntity = 'transfer';

export type StockTransfersApiDeps = StockDocumentMutationDeps<TransferStockDocumentEntity>;

export const createStockTransfersApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockTransfersApiDeps) => ({
  list: async (filters: TransferFilters = {}): Promise<TransfersResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'transfer',
      await api.webAdmin.get<TransfersResponse>('/stock/transfers', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<TransferDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'transfer',
      await api.webAdmin.get<TransferDetailResponse>(`/stock/transfers/${id}`),
      (response) => response.transfer.id,
      (response) => response.transfer.updated_at,
    );
  },

  create: async (
    data: CreateTransferRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<TransferDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'transfer',
      await api.webAdmin.post<TransferDetailResponse>('/stock/transfers', data, options),
      (response) => response.transfer.id,
      (response) => response.transfer.updated_at,
    );
  },

  update: (
    id: string,
    data: UpdateTransferRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<TransferDetailResponse> =>
    api.webAdmin
      .put<TransferDetailResponse>(
        `/stock/transfers/${id}`,
        data,
        withExpectedUpdatedAt('transfer', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'transfer',
          response,
          (current) => current.transfer.id,
          (current) => current.transfer.updated_at,
        ),
      ),

  confirm: (id: string, options?: StockDocumentMutationOptions): Promise<TransferDetailResponse> =>
    api.webAdmin
      .post<TransferDetailResponse>(
        `/stock/transfers/${id}/confirm`,
        undefined,
        withExpectedUpdatedAt('transfer', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'transfer',
          response,
          (current) => current.transfer.id,
          (current) => current.transfer.updated_at,
        ),
      ),

  cancel: (
    id: string,
    data?: CancelTransferRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<TransferDetailResponse> =>
    api.webAdmin
      .post<TransferDetailResponse>(
        `/stock/transfers/${id}/cancel`,
        data,
        withExpectedUpdatedAt('transfer', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'transfer',
          response,
          (current) => current.transfer.id,
          (current) => current.transfer.updated_at,
        ),
      ),

  deleteDraft: (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<{ success: boolean; deleted?: boolean }> =>
    api.webAdmin
      .delete<{
        success: boolean;
        deleted?: boolean;
      }>(`/stock/transfers/${id}`, withExpectedUpdatedAt('transfer', id, options))
      .then((response) => {
        forgetStockDocumentUpdatedAt('transfer', id);
        return response;
      }),

  transferReconciliationXlsxUrl: (transferId: string): string =>
    `/api/web-admin/stock/transfers/${transferId}/reconciliation/xlsx`,
});
